import type {PropsWithChildren} from 'react';
import {useCallback} from 'react';
import {useState} from 'react';
import {useMemo} from 'react';
import {useContext} from 'react';
import React, {createContext, useEffect} from 'react';
import type {Maybe} from '../../../graphql';
import {useTranslations} from '../../hooks/use-translations';
import {InterceptContext} from './Intercept';
import type {
  ErrorType,
  FakeError,
  IWrapperProvider,
  JSONObject,
  JSONValue,
  WrapperHandler,
  allSchemas,
} from './types';
import {fakeOrValidation, isValidError} from './types';

export const FieldContext = createContext({});

interface WrapperStateItem {
  error: FakeError | undefined;
  schema: allSchemas | undefined;
  state: JSONObject | JSONValue;
}

interface IProps {
  translationPrefix: string;
  showErrors?: IWrapperProvider[`showErrors`];
  onUpdate?: WrapperHandler<any>;
  disabled?: boolean;
}

const Field = ({
  translationPrefix,
  showErrors = false,
  onUpdate,
  children,
  disabled = false,
}: PropsWithChildren<IProps>) => {
  const [data, setData] = useState<Record<string, WrapperStateItem>>({});
  const translate = useTranslations();
  const intercept = useContext(InterceptContext);

  const validateField = useCallback(
    (item: WrapperStateItem) => {
      if (!item.schema) return undefined;

      try {
        item.schema.validateSync(item.state);
        if (item.error) item.error = undefined;
        return item.error;
      } catch (err) {
        if (!isValidError(err)) return undefined;

        if (fakeOrValidation(err)) {
          if (!err.internals && !err.self) return undefined;
          return err;
        }
        return {
          self: err,
          internals: undefined,
        };
      }
    },
    [isValidError, fakeOrValidation],
  );

  const genericHandler = useCallback(
    (tag: string, value: JSONValue | Record<string, JSONValue>) => {
      setData((prevData) => {
        if (!(tag in prevData)) return prevData;

        const prevStateItem = prevData[tag]!;
        if (prevStateItem!.state === value) return prevData;

        const newItem = {...prevStateItem, state: value};
        let error = validateField(newItem);
        if (!error?.internals && !error?.self) error = undefined;
        return {...prevData, [tag]: {...newItem, error}};
      });
    },
    [setData, validateField],
  );

  const output = useMemo(() => {
    const state = Object.fromEntries(
      Object.entries(data).map(([key, wState]) => [key, wState.state]),
    );
    const errorEntries = Object.entries(data)
      .map(([key, wState]) => [key, wState.error])
      .filter(([, value]) => value !== undefined);
    const error = errorEntries.length
      ? (Object.fromEntries(errorEntries) as ErrorType)
      : undefined;

    return {
      state,
      error,
      subErrors: error
        ? ({self: undefined, internals: error} as FakeError)
        : undefined,
    };
  }, [data]);

  useEffect(() => {
    if (!Object.keys(output.state).length && !output.error) return;
    onUpdate && onUpdate(output.state, output.error);
  }, [output]);

  const registerField = useCallback(
    (
      tag: string,
      fieldSchema: allSchemas | undefined,
      remove: boolean = false,
      initial: any,
    ) => {
      setData((prevData) => {
        const newData = {...prevData};

        if (tag in newData) {
          // remove field
          if (remove) {
            delete newData[tag];
            return newData;
          }
          // update data schema
          newData[tag]!.schema = fieldSchema;
          return newData;
        }

        // add field
        const newDataItem: WrapperStateItem = {
          schema: fieldSchema,
          state: initial,
          error: undefined,
        };

        newData[tag] = {
          ...newDataItem,
          error: validateField(newDataItem),
        };

        return newData;
      });
    },
    [setData, validateField],
  );

  const getLabel = useCallback(
    (tag: string, required?: boolean, label?: Maybe<string>) => {
      if (label) {
        return `${label} ${required ? `*` : ``}`;
      }
      return `${translate(`${translationPrefix}.${tag}`)} ${
        required ? `*` : ``
      }`;
    },
    [translate, translationPrefix],
  );

  const provider: IWrapperProvider = useMemo(
    () => ({
      translationPrefix,
      state: output.state,
      errors: intercept ? intercept.errors : output.subErrors,
      genericHandler,
      getLabel,
      showErrors: intercept ? intercept.showErrors : showErrors,
      registerField,
      disabled,
    }),
    [
      translationPrefix,
      output.state,
      output.subErrors,
      intercept,
      intercept?.showErrors,
      showErrors,
      disabled,
      genericHandler,
      getLabel,
      registerField,
    ],
  );

  return (
    <FieldContext.Provider value={provider}>{children}</FieldContext.Provider>
  );
};

export default Field;
